import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { toast } from "sonner";
import { logApp } from "@/utils/logApp";
import {
	formatErrorForDisplay,
	isApiError,
	logError,
} from "../../utils/error-transformer";

/**
 * Global error handler for TanStack Query
 * Handles all query and mutation errors consistently
 */
function handleQueryError(error: unknown): void {
	// Log error (respects debug mode)
	if (isApiError(error)) {
		logError(error);
	}

	// Log to Datadog RUM (browser only)
	if (
		typeof window !== "undefined" &&
		window.DD_RUM &&
		error instanceof Error
	) {
		try {
			window.DD_RUM.addError(error, {
				type: "tanstack_query_error",
				code: isApiError(error) ? error.code : "UNKNOWN",
			});
		} catch (ddError) {
			console.warn("Failed to log error to Datadog:", ddError);
			logApp({
				level: "error",
				message: `Failed to log error to Datadog: ${ddError}`,
			});
		}
	}

	// Show toast for user-facing errors
	// Only show for non-network errors (network errors are handled by interceptors)
	if (isApiError(error) && error.code !== "NETWORK_ERROR") {
		const { title, message } = formatErrorForDisplay(error);

		toast.error(title, {
			description: message,
			duration: 5000,
		});
	}
}

/**
 * Create QueryClient with default configuration
 */
export function getContext() {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				// Avoid crashing SSR / hydration when a query fails (e.g. API down, ECONNREFUSED)
				throwOnError: false,

				// Retry configuration
				retry: (failureCount, error) => {
					// Don't retry on 4xx errors (client errors)
					if (isApiError(error) && error.status && error.status < 500) {
						return false;
					}

					// Retry up to 3 times for 5xx and network errors
					return failureCount < 3;
				},

				// Stale time - data is considered fresh for 5 minutes
				staleTime: 5 * 60 * 1000,

				// Cache time - unused data is removed after 10 minutes
				gcTime: 10 * 60 * 1000,

				// Refetch on window focus for real-time data
				refetchOnWindowFocus: true,

				// Refetch on reconnect
				refetchOnReconnect: true,

				// Global error handler
				// Note: Individual queries can override this with their own onError
			},

			mutations: {
				// Global mutation error handler
				// Note: Individual mutations can override this with their own onError
				onError: handleQueryError,
			},
		},
	});

	return {
		queryClient,
	};
}

export const queryClient = getContext().queryClient;

export function Provider({
	children,
	queryClient,
}: {
	children: React.ReactNode;
	queryClient: QueryClient;
}) {
	return (
		<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
	);
}
